import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import './HoverTooltip.css';
import { isCapacitor } from '../../utils/platform';

/**
 * HoverTooltip Component
 * 
 * A reusable tooltip component that appears on hover.
 * Wraps any element and shows a tooltip when the user hovers over it.
 * 
 * @param {ReactNode} children - The element that triggers the tooltip
 * @param {string} text - The tooltip text to display
 * @param {string} position - Where to show tooltip: 'top', 'bottom', 'left', 'right'
 * @param {boolean} arrow - Show arrow pointing to element
 * @param {number} delay - Delay before showing tooltip in milliseconds
 * @param {string} className - Additional CSS classes for the tooltip
 * @param {boolean} disabled - Disable the tooltip
 */
const HoverTooltip = ({
  children,
  text,
  position = 'top',
  arrow = true,
  delay = 0,
  className = '',
  disabled = false,
  forceShow = false
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState(position);
  const [coords, setCoords] = useState({ top: 0, left: 0, transform: 'none' });
  const timeoutRef = useRef(null);
  const raf1Ref = useRef(null);
  const raf2Ref = useRef(null);
  const containerRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (raf1Ref.current) cancelAnimationFrame(raf1Ref.current);
      if (raf2Ref.current) cancelAnimationFrame(raf2Ref.current);
    };
  }, []);

  const handleMouseEnter = () => {
    if (disabled || !text) return;

    const show = () => {
      setIsVisible(true);
      raf1Ref.current = requestAnimationFrame(() => {
        adjustPosition();
        // Schedule a second frame to ensure portal content is measured
        raf2Ref.current = requestAnimationFrame(adjustPosition);
      });
    };

    if (delay > 0) {
      timeoutRef.current = setTimeout(show, delay);
    } else {
      show();
    }
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsVisible(false);
  };

  // Adjust tooltip position to avoid viewport edges
  const adjustPosition = () => {
    if (!containerRef.current || !tooltipRef.current) return;

    const container = containerRef.current;
    const tooltip = tooltipRef.current;
    const rect = container.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();

    let newPosition = position;

    // Flip if overflowing viewport
    if (position === 'top' && rect.top - tooltipRect.height < 10) {
      newPosition = 'bottom';
    } else if (position === 'bottom' && rect.bottom + tooltipRect.height > window.innerHeight - 10) {
      newPosition = 'top';
    } else if (position === 'left' && rect.left - tooltipRect.width < 10) {
      newPosition = 'right';
    } else if (position === 'right' && rect.right + tooltipRect.width > window.innerWidth - 10) {
      newPosition = 'left';
    }

    if (newPosition !== tooltipPosition) {
      setTooltipPosition(newPosition);
    }

    // Compute fixed coordinates
    const GAP = 8;
    let top = 0;
    let left = 0;

    if (newPosition === 'top') {
      top = Math.max(0, rect.top - tooltipRect.height - GAP);
      left = rect.left + rect.width / 2 - tooltipRect.width / 2;
    } else if (newPosition === 'bottom') {
      top = rect.bottom + GAP;
      left = rect.left + rect.width / 2 - tooltipRect.width / 2;
    } else if (newPosition === 'left') {
      top = rect.top + rect.height / 2 - tooltipRect.height / 2;
      left = Math.max(0, rect.left - tooltipRect.width - GAP);
    } else if (newPosition === 'right') {
      top = rect.top + rect.height / 2 - tooltipRect.height / 2;
      left = rect.right + GAP;
    }

    // Constrain within viewport horizontally
    left = Math.min(Math.max(4, left), window.innerWidth - tooltipRect.width - 4);

    setCoords({ top, left, transform: 'none' });
  };

  // Recompute on visibility, text, position changes and on scroll/resize
  useEffect(() => {
    if (!isVisible) return;
    const onScrollOrResize = () => adjustPosition();
    // Initial compute after mount
    const id1 = requestAnimationFrame(adjustPosition);
    const id2 = requestAnimationFrame(adjustPosition);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      cancelAnimationFrame(id1);
      cancelAnimationFrame(id2);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isVisible, text, position]);

  // Don't render tooltip on small screens, Capacitor, disabled or no text
  const isSmallScreen = typeof window !== 'undefined' && window.innerWidth < 500;
  if (disabled || !text || isCapacitor || (isSmallScreen && !forceShow)) {
    return <>{children}</>;
  }

  return (
    <div 
      className="hover-tooltip-container"
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isVisible && createPortal(
        <div
          ref={tooltipRef}
          className={`
            hover-tooltip 
            hover-tooltip-${tooltipPosition}
            ${arrow ? 'hover-tooltip-arrow' : ''}
            ${className}
          `}
          role="tooltip"
          aria-hidden={!isVisible}
          style={{
            position: 'fixed',
            top: `${coords.top}px`,
            left: `${coords.left}px`,
            bottom: 'auto',
            right: 'auto',
            transform: 'none',
            zIndex: 10010,
          }}
        >
          <span className="hover-tooltip-text">{text}</span>
        </div>,
        document.body
      )}
    </div>
  );
};

HoverTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  text: PropTypes.string,
  position: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  arrow: PropTypes.bool,
  delay: PropTypes.number,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  forceShow: PropTypes.bool
};

export default HoverTooltip;